// Supabase browser client (singleton). The anon key is public by design; access
// is enforced by Row Level Security in the database (see supabase/migrations).
import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// In local dev without Supabase env vars, fall back to a no-op mock so the app
// still renders. It mirrors the surface the app uses (from / rpc / auth).
let supabase: any;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    "Supabase environment variables not configured - using mock client for development",
  );
  const noData = () => Promise.resolve({ data: null, error: null });
  supabase = {
    from: () => ({
      insert: noData,
      select: () => Promise.resolve({ data: [], error: null }),
      update: noData,
      delete: noData,
    }),
    rpc: noData,
    auth: {
      getSession: () =>
        Promise.resolve({ data: { session: null }, error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signInWithPassword: noData,
      signOut: () => Promise.resolve({ error: null }),
      updateUser: noData,
      resetPasswordForEmail: noData,
      onAuthStateChange: () => ({
        data: { subscription: { unsubscribe: () => {} } },
      }),
    },
  };
} else {
  // Single GoTrue-enabled client: persist + auto-refresh the admin session,
  // and detect the recovery token Supabase puts in the URL on password reset.
  supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  });
}

export { supabase };
